import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Loader2, Check, IndianRupee, Infinity as InfinityIcon, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

// Web paywall — bottom sheet that opens over the current route so a 402
// from any tracker/AI endpoint (or the "Start 7-day Trial" ribbon) never
// yanks the user out of the flow they were in. iOS/Android native paths
// still go through <NativePaywall /> because Apple/Google require IAP.

const PLAN_META = {
  ai_yearly: {
    label: 'Annual',
    price: '1,999',
    sub: 'for a full year · ~₹167/mo',
    cta: 'Upgrade — ₹1,999/year',
    accent: 'from-[#FFF9F0] to-white border-[#F4B333]/30',
    priceColor: '#1C1917',
    badge: null,
  },
  ai_lifetime: {
    label: 'Lifetime',
    price: '2,999',
    sub: 'one-time · never pay again',
    cta: 'Get Lifetime — ₹2,999',
    accent: 'from-[#FFE6CC] via-[#FFF3E0] to-[#FFEDD5] border-[#E05A47]/40',
    priceColor: '#B83826',
    badge: 'BEST VALUE',
  },
};

const FEATURES = [
  'Unlimited AI Saheli voice chats',
  'Cry decoder + meal analyser',
  'All Trackers + growth logs',
  'Priority doctor video consults',
];

const WebPaywallSheet = ({ open, onClose, source, mode }) => {
  const [processingPlan, setProcessingPlan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setStatus(r.data);
    } catch {
      // silent — the sheet still works even if status is unknown
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const upgrade = async (planId) => {
    setProcessingPlan(planId);
    try {
      const r = await axios.post(
        `${API}/subscription/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true },
      );
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
      setProcessingPlan(null);
    }
  };

  const startTrial = async () => {
    setProcessingPlan('trial');
    try {
      const r = await axios.post(
        `${API}/subscription/trial/start`,
        { origin_url: window.location.origin },
        { withCredentials: true },
      );
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start trial');
      setProcessingPlan(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="web-paywall-backdrop"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[210] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            data-testid="web-paywall-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="web-paywall-title"
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"
          >
            {/* handle bar */}
            <div className="pt-2 flex justify-center sm:hidden">
              <div className="w-10 h-1 rounded-full bg-[#E8E4DB]" />
            </div>

            <button
              onClick={onClose}
              data-testid="web-paywall-close-btn"
              aria-label="Close paywall"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#FAF7F2] flex items-center justify-center active:scale-95"
            >
              <X className="w-4 h-4 text-[#57534E]" />
            </button>

            <div className="p-5 pb-8">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#E05A47] to-[#F4B333] flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <h2 id="web-paywall-title" className="text-2xl font-black text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>
                  Aayi Pro
                </h2>
              </div>
              <p className="text-sm text-[#57534E] mb-4">
                {source === 'tracker'
                  ? 'Trackers are part of Aayi Pro — unlock every log + all AI.'
                  : 'Unlock unlimited AI, every tracker, and priority doctor care.'}
              </p>

              <ul className="grid grid-cols-2 gap-x-3 gap-y-2 mb-5" data-testid="web-paywall-features">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-1.5 text-[12px] text-[#1C1917]">
                    <Check className="w-3.5 h-3.5 text-[#3D9970] flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* Trial CTA — only when the caller opened the sheet for trial */}
              {mode === 'trial' && (
                <button
                  onClick={startTrial}
                  disabled={!!processingPlan}
                  data-testid="web-paywall-trial-btn"
                  className="w-full mb-3 py-3.5 rounded-2xl bg-gradient-to-r from-[#3D9970] to-[#2F7A57] text-white font-bold shadow-[0_6px_18px_rgba(61,153,112,0.28)] disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {processingPlan === 'trial' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Start 7-day free trial</>
                  )}
                </button>
              )}

              <div className="grid gap-3 mb-4" data-testid="web-paywall-plans">
                {['ai_lifetime', 'ai_yearly'].map((id) => {
                  const meta = PLAN_META[id];
                  const busy = processingPlan === id;
                  return (
                    <button
                      key={id}
                      onClick={() => upgrade(id)}
                      disabled={!!processingPlan}
                      data-testid={`web-paywall-plan-${id}`}
                      className={`relative overflow-hidden rounded-2xl p-4 text-left bg-gradient-to-br ${meta.accent} border disabled:opacity-60 active:scale-[0.99]`}
                    >
                      {meta.badge && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#1C1917] text-white text-[9px] font-black tracking-wider">
                          {meta.badge}
                        </span>
                      )}
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#B83826] font-bold mb-1 flex items-center gap-1">
                        {id === 'ai_lifetime' && <InfinityIcon className="w-3 h-3" />}
                        {meta.label}
                      </p>
                      <div className="flex items-baseline gap-1">
                        <IndianRupee className="w-4 h-4" style={{ color: meta.priceColor }} />
                        <p className="text-3xl font-black" style={{ fontFamily: 'Outfit', color: meta.priceColor }}>{meta.price}</p>
                      </div>
                      <p className="text-[11px] text-[#57534E]">{meta.sub}</p>
                      <p className="mt-2 text-[11px] font-bold text-[#B83826] flex items-center gap-1">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crown className="w-3 h-3" />}
                        {busy ? 'Opening checkout…' : meta.cta}
                      </p>
                    </button>
                  );
                })}
              </div>

              {loading && (
                <div className="flex justify-center py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#E05A47]" />
                </div>
              )}

              {status?.free_uses_left != null && !status?.active && (
                <p className="text-[11px] text-[#78716C] text-center mb-2" data-testid="web-paywall-free-uses">
                  {status.free_uses_left} free AI use{status.free_uses_left === 1 ? '' : 's'} left
                </p>
              )}

              <p className="text-[10px] text-[#A8A29E] text-center leading-relaxed">
                Secure payment via Stripe · Cancel anytime · UPI / card / net-banking
              </p>

              <div className="mt-3 pt-3 border-t border-[#E8E4DB] flex flex-wrap gap-x-3 gap-y-1 justify-center text-[10px] text-[#78716C]">
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Terms</a>
                <span>·</span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Privacy</a>
                <span>·</span>
                <button
                  onClick={onClose}
                  data-testid="web-paywall-later-btn"
                  className="underline font-semibold"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WebPaywallSheet;
